import { getReceiptLabel } from '../../utils/receiptLabels';
import {
  computeThermalFontSizePx,
  thermalMediaWidthMm
} from './thermalReceiptConfig';
import {
  buildThermalReceiptLayoutData,
  renderThermalReceiptBodyHtml,
  THERMAL_RECEIPT_LAYOUT_CSS
} from './thermalReceiptHtmlLayout';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Compact HTML for browser / Electron silent thermal print.
 *
 * Page box = mediaWidthMm (~72 on "80 mm" rolls; driver forms often 72×…),
 * content band = printableWidthMm + safety inset. Height stays auto / measured.
 */
export function renderThermalReceiptHtml(documentModel, options = {}) {
  const { config, viewModel } = documentModel;
  const profile = config.profile;
  const mediaMm = thermalMediaWidthMm(profile);
  const printMm =
    Number(profile.printableWidthMm) > 0
      ? Number(profile.printableWidthMm)
      : mediaMm;
  const sideMarginMm =
    profile.sideMarginMm != null
      ? Number(profile.sideMarginMm)
      : Math.max(0, (mediaMm - printMm) / 2);
  const safetyInsetMm =
    Number(profile.safetyInsetMm) >= 0 ? Number(profile.safetyInsetMm) : 1;
  const language = config.language || 'fr';
  const fontSize = `${computeThermalFontSizePx(profile, 0.96, language)}px`;
  const isRtl = language === 'ar';
  const dir = isRtl ? 'rtl' : 'ltr';
  const title =
    options.title ||
    viewModel.receipt.title ||
    getReceiptLabel(
      config.merchantCopy ? 'htmlTitleMerchant' : 'htmlTitleClient',
      language
    );

  const layoutData = buildThermalReceiptLayoutData(viewModel, config);
  const bodyHtml = renderThermalReceiptBodyHtml(layoutData);
  const preview = Boolean(options.preview);
  const arFont = '"Segoe UI", "Noto Sans Arabic", Tahoma, Arial, sans-serif';
  const latFont = '"Courier New", Courier, monospace';

  return `<!DOCTYPE html>
<html lang="${escapeHtml(language)}" dir="${dir}">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=${mediaMm}mm" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page {
      size: ${mediaMm}mm auto;
      margin: 0;
    }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #000;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    body.thermal-paper {
      width: ${mediaMm}mm;
      max-width: ${mediaMm}mm;
      min-width: 0;
      margin: 0 auto;
      padding: 0 ${sideMarginMm}mm;
      box-sizing: border-box;
      overflow-x: hidden;
    }
    @media print {
      html, body, body.thermal-paper {
        width: ${mediaMm}mm !important;
        max-width: ${mediaMm}mm !important;
        min-width: 0 !important;
      }
    }
    .thermal-print-area {
      width: 100%;
      max-width: ${printMm}mm;
      margin: 0 auto;
      padding: 1.5mm ${safetyInsetMm}mm 2.5mm;
      box-sizing: border-box;
      font-family: ${isRtl ? arFont : latFont};
      font-size: ${fontSize};
      line-height: 1.12;
      direction: ${dir};
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    .thermal-print-area img {
      max-width: 100%;
      height: auto;
    }
    .thermal-print-area table {
      width: 100%;
      max-width: 100%;
    }
    ${THERMAL_RECEIPT_LAYOUT_CSS}
    ${preview ? `@media screen {
      html, body {
        -webkit-font-smoothing: none;
        -moz-osx-font-smoothing: unset;
        text-rendering: geometricPrecision;
      }
    }` : ''}
    @media print {
      html, body.thermal-paper {
        width: ${mediaMm}mm;
        max-width: ${mediaMm}mm;
      }
      .thermal-print-area {
        width: 100%;
        max-width: ${printMm}mm;
        margin: 0 auto;
      }
    }
  </style>
</head>
<body class="thermal-paper">
  <div class="thermal-print-area">
    ${bodyHtml}
  </div>
</body>
</html>`;
}
